import { useState, useEffect, useRef, useCallback } from "react";
import { getItem } from "../../services/Utils";
import usePageTitle from "../../hooks/usePageTitle";
import UserDetailsTab from "../../components/settings/UserDetailsTab";
// import BusinessDetailsTab from "../../components/settings/BusinessDetailsTab";
// import ManagePaymentTab from "../../components/settings/ManagePaymentTab";
import DatabaseConnectionTab from "../../components/settings/DatabaseConnectionTab";
import WorkflowActivationTab from "../../components/settings/WorkflowActivationTab";
import { APIs } from "../../services/Apis";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  CircleStackIcon,
  PlayCircleIcon,
  SparklesIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { Logo } from "../../components/Logo";
import BrandKitTab from "../../components/settings/BrandKitTab";
import { SettingsShimmerLoader } from "../../components/loaders/SettingsShimmerLoader";
import { SampleDataBanner } from "../../components/SampleDataBanner";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const TABS = {
  USER_DETAILS: "user-details",
  // BUSINESS_DETAILS: "business-details",
  DATABASE_CONNECTION: "database-connection",
  // MANAGE_PAYMENT: "manage-payment",
  WORKFLOW_ACTIVATION: "workflow-activation",
  BRAND_KIT: "brand-kit",
};

const TAB_CONFIG = [
  { id: TABS.USER_DETAILS, name: "User Details", icon: UserIcon },
  { id: TABS.BRAND_KIT, name: "Brand Kit", icon: SparklesIcon },
  {
    id: TABS.DATABASE_CONNECTION,
    name: "Database Connection",
    icon: CircleStackIcon,
  },
  // { id: TABS.MANAGE_PAYMENT, name: "Manage Payment", icon: CreditCardIcon },
  {
    id: TABS.WORKFLOW_ACTIVATION,
    name: "Workflow Activation",
    icon: PlayCircleIcon,
  },
];

export default function SettingsPage() {
  usePageTitle("Settings");
  const [activeTab, setActiveTab] = useState(TABS.USER_DETAILS);
  const [loading, setLoading] = useState(true);
  const [settingsData, setSettingsData] = useState(null);
  const [globalActiveWorkFlow, setGlobalActiveWorkFlow] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const containerRef = useRef(null);

  const fetchSettingsDetails = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await APIs.getSettingsDetails();

      if (response.success) {
        setSettingsData(response);
        setGlobalActiveWorkFlow(response.active === true);
      } else {
        toast.error(response.message || "Failed to load settings details");
      }
    } catch (err) {
      console.error("Error fetching settings details:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettingsDetails();
  }, [fetchSettingsDetails]);

  useEffect(() => {
    // Read tab from query param or hash and set active tab
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get("tab");
      if (tabParam && Object.values(TABS).includes(tabParam)) {
        setActiveTab(tabParam);
        return;
      }

      const hash = window.location.hash.slice(1); // Remove the '#' character
      if (hash && Object.values(TABS).includes(hash)) {
        setActiveTab(hash);
      }
    };

    // Set initial tab from hash or query
    handleUrlChange();

    // Listen for URL changes
    window.addEventListener("hashchange", handleUrlChange);
    window.addEventListener("popstate", handleUrlChange);

    return () => {
      window.removeEventListener("hashchange", handleUrlChange);
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, []);

  if (loading) {
    return <SettingsShimmerLoader />;
  }

  const handleToggleWorkflow = async (active) => {
    try {
      const response = await APIs.updateSettings(active);
      if (response.success) {
        setGlobalActiveWorkFlow(active);
        toast.success(
          `Workflow ${active ? "activated" : "deactivated"} successfully`,
        );
      } else {
        toast.error(response.message || "Failed to update workflow status");
      }
    } catch (err) {
      console.error("Error updating workflow status:", err);
    }
  };

  const sidebarContent = (
    <>
      <div className="relative flex h-16 shrink-0 items-center px-4">
        <a
          href="/dashboard"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          aria-label="Home"
        >
          <Logo className="h-4 w-auto" />
          <span
            className="text-2xl font-semibold text-gray-900"
            style={{ fontFamily: "'Questrial', sans-serif" }}
          >
            fluvo
          </span>
        </a>
      </div>
      <nav className="relative flex flex-1 flex-col px-4 mt-4">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <div className="text-xs/6 font-semibold text-gray-400">
              Settings
            </div>
            <ul role="list" className="-mx-2 mt-2 space-y-1">
              {TAB_CONFIG.map((tab) => {
                const current = activeTab === tab.id;
                return (
                  <li key={tab.id}>
                    <button
                      onClick={() => {
                        setActiveTab(tab.id);
                        const newUrl = new URL(window.location);
                        newUrl.searchParams.delete("tab");
                        newUrl.hash = tab.id;
                        window.history.pushState({}, "", newUrl);
                        setSidebarOpen(false);
                      }}
                      className={classNames(
                        current
                          ? "bg-gray-50 text-gray-900"
                          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                        "group flex w-full gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-left cursor-pointer",
                      )}
                    >
                      <tab.icon
                        className={classNames(
                          current
                            ? "text-gray-900"
                            : "text-gray-400 group-hover:text-gray-900",
                          "h-6 w-6 shrink-0",
                        )}
                        aria-hidden="true"
                      />
                      <span className="truncate">{tab.name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </li>

          <li className="-mx-4 mt-auto flex flex-col">
            <a
              href="/dashboard"
              className="flex items-center gap-x-4 px-4 py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50"
            >
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white">
                <ArrowLeftIcon className="size-4 text-gray-500" />
              </div>
              <span aria-hidden="true">Back to Dashboard</span>
            </a>
          </li>
        </ul>
      </nav>
    </>
  );

  const showSampleBanner = settingsData?.database?.isSample === true;

  return (
    <>
      <div>
        {showSampleBanner && <SampleDataBanner />}
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>

              <div className="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white pb-2">
                {sidebarContent}
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div
          className={classNames(
            "hidden lg:fixed lg:bottom-0 lg:z-50 lg:flex lg:w-72 lg:flex-col",
            showSampleBanner ? "lg:top-9" : "lg:top-0",
          )}
        >
          <div className="relative flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white">
            {sidebarContent}
          </div>
        </div>

        <div
          className={classNames(
            "sticky z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden border-b border-gray-200",
            showSampleBanner ? "top-9" : "top-0",
          )}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="relative -m-2.5 p-2.5 text-gray-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
          <div className="relative flex-1 text-sm/6 font-semibold text-gray-900">
            <Logo className="h-6 w-auto inline-block mr-2" />
            fluvo
          </div>
        </div>

        <div
          ref={containerRef}
          className="lg:pl-72"
          style={{
            display: "flex",
            flexDirection: "row",
            height: showSampleBanner ? "calc(100vh - 36px)" : "100vh",
            minHeight: 0,
          }}
        >
          <main
            className="bg-white"
            style={{ flex: 1, minWidth: 0, overflowY: "auto" }}
          >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                <p className="mt-2 text-sm text-gray-600">
                  Manage your account settings and preferences
                </p>
              </div>

              <div className="mt-8">
                {activeTab === TABS.USER_DETAILS && (
                  <UserDetailsTab
                    initialData={{
                      name: settingsData?.userName || "",
                      email: settingsData?.userEmail || "",
                      provider: settingsData?.provider || "",
                    }}
                  />
                )}
                {activeTab === TABS.BRAND_KIT && (
                  <BrandKitTab
                    initialData={{
                      companyName: settingsData?.companyName || "",
                      companyDescription:
                        settingsData?.companyDescription || "",
                      companyLogo: settingsData?.companyLogo || "",
                      companyColors: settingsData?.companyColors || [],
                      companyTone: settingsData?.companyTone || "",
                    }}
                  />
                )}
                {/* {activeTab === TABS.BUSINESS_DETAILS && (
                  <BusinessDetailsTab
                    initialData={{
                      businessName: settingsData?.businessName || "",
                      websiteUrl: settingsData?.businessUrl || "",
                      businessDescription: settingsData?.businessDescription || "",
                      stakeholders: settingsData?.stakeholders || [
                        { id: 1, name: "", description: "" },
                      ],
                    }}
                  />
                )} */}
                {activeTab === TABS.DATABASE_CONNECTION && (
                  <DatabaseConnectionTab
                    initialData={
                      settingsData?.database && !settingsData.database.isSample
                        ? {
                            dbUsername:
                              settingsData.database.databaseUsername || "",
                            dbPassword:
                              settingsData.database.databasePassword || "",
                            dbUrl: settingsData.database.databaseUrl || "",
                            dbType: settingsData.database.databaseType || "",
                            dbName: settingsData.database.databaseName || "",
                            dbPort: settingsData.database.databasePort || "",
                          }
                        : {}
                    }
                    isSample={settingsData?.database?.isSample === true}
                    hasExistingDatabase={
                      !!settingsData?.database &&
                      !settingsData.database.isSample
                    }
                    onSampleGenerated={() => fetchSettingsDetails(false)}
                  />
                )}
                {/* {activeTab === TABS.MANAGE_PAYMENT && <ManagePaymentTab />} */}
                {activeTab === TABS.WORKFLOW_ACTIVATION && (
                  <WorkflowActivationTab
                    globalActiveWorkFlow={globalActiveWorkFlow}
                    onToggle={handleToggleWorkflow}
                    isDatabaseConfigured={!!settingsData?.database}
                  />
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
